
import { defineComponent, PropType } from 'vue'
import type { FetchResult } from 'apollo-link'

import RequestSlackConnectMutation from '~/apollo/mutations/owner/requestSlackConnect.gql'
import ResendSlackConnectInviteMutation from '~/apollo/mutations/owner/resendSlackConnectInvite.gql'
import type {
  RequestSlackConnectMutation as RequestSlackConnectMutationType,
  ResendSlackConnectInviteMutation as ResendSlackConnectInviteMutationType
} from '~/types/types'

interface SlackConnectChannel {
  status?: string | null
  channelName?: string | null
}

export default defineComponent({
  name: 'JoinSlackChannelModal',
  props: {
    email: {
      type: String,
      required: true
    },
    ownerId: {
      type: String,
      required: true
    },
    slackConnectChannel: {
      type: Object as PropType<SlackConnectChannel | null>,
      default: null
    }
  },
  data() {
    return {
      submitting: false
    }
  },
  computed: {
    isConnected(): boolean {
      return this.slackConnectChannel?.status === 'CONNECTED'
    },
    showResendModal(): boolean {
      const status = this.slackConnectChannel?.status
      // NOT_STARTED means an invite record exists but hasn't been processed yet.
      // Without it, users could fire duplicate join requests before the invite is sent.
      return status === 'NOT_STARTED' || status === 'INVITE_SENT' || status === 'FAILED'
    },
    slackChannelUrl(): string {
      const channelName = this.slackConnectChannel?.channelName
      return `https://slack.com/app_redirect?channel=${channelName}`
    },
    modalTitle(): string {
      if (this.isConnected) return 'Open Slack channel'
      return this.showResendModal ? 'Resend Slack invite' : 'Join your Slack channel'
    },
    submitLabel(): string {
      if (this.submitting) return this.showResendModal ? 'Resending…' : 'Sending invite…'
      return this.showResendModal ? 'Resend invite' : 'Join channel'
    }
  },
  methods: {
    openSlackChannel(): void {
      window.open(this.slackChannelUrl, '_blank', 'noopener,noreferrer')
      this.$emit('close')
    },
    async joinChannel(): Promise<void> {
      this.submitting = true
      try {
        const response: FetchResult<RequestSlackConnectMutationType> =
          await this.$applyGraphqlMutation(RequestSlackConnectMutation, {
            input: {
              ownerId: this.ownerId,
              email: this.email
            }
          })

        if (response?.data?.requestSlackConnect?.ok) {
          this.$toast.success('Invite sent! Check your email to join the Slack channel.')
          this.$emit('success')
        }
      } catch (e) {
        this.$logErrorAndToast(
          e as Error,
          'Something went wrong while sending the invite. Please try again.'
        )
      } finally {
        this.submitting = false
      }
    },
    async resendInvite(): Promise<void> {
      this.submitting = true
      try {
        const response: FetchResult<ResendSlackConnectInviteMutationType> =
          await this.$applyGraphqlMutation(ResendSlackConnectInviteMutation, {
            input: { ownerId: this.ownerId }
          })

        if (response?.data?.resendSlackConnectInvite?.ok) {
          this.$toast.success('Invite resent! Check your email to join the Slack channel.')
          this.$emit('success')
        }
      } catch (e) {
        this.$logErrorAndToast(e as Error, 'Something went wrong while resending the invite.')
      } finally {
        this.submitting = false
      }
    }
  }
})
